/**
 * Expense Query Hooks
 *
 * Read-only hooks for fetching expense data.
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../../infrastructure/api/client";
import {
  ApiError,
  type ExpenseResponseDto,
  type MemberBalanceDto,
  type SettlementDto,
  type BalanceSummaryDto,
} from "@tabipla/shared";
import { expenseKeys, balanceKeys } from "../queryKeys";

/**
 * Fetch expenses for a session
 */
export function useExpenses(groupId: string | undefined, sessionId: string | undefined) {
  return useQuery({
    queryKey: expenseKeys.session(groupId ?? "", sessionId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<ExpenseResponseDto[]>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch expenses");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId,
  });
}

/**
 * Fetch a single expense
 */
export function useExpense(
  groupId: string | undefined,
  sessionId: string | undefined,
  expenseId: string | undefined
) {
  return useQuery({
    queryKey: expenseKeys.detail(groupId ?? "", sessionId ?? "", expenseId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<ExpenseResponseDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses/${expenseId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch expense");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId && !!expenseId,
  });
}

/**
 * Fetch balances for a session
 */
export function useBalances(groupId: string | undefined, sessionId: string | undefined) {
  return useQuery({
    queryKey: expenseKeys.balances(groupId ?? "", sessionId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<MemberBalanceDto[]>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses/balances`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch balances");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId,
  });
}

/**
 * Fetch settlements for a session
 */
export function useSettlements(groupId: string | undefined, sessionId: string | undefined) {
  return useQuery({
    queryKey: expenseKeys.settlements(groupId ?? "", sessionId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<SettlementDto[]>(
        `/api/groups/${groupId}/sessions/${sessionId}/expenses/settlements`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch settlements");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId,
  });
}

/**
 * Fetch balance summary for the current user across all groups
 */
export function useBalanceSummary() {
  return useQuery({
    queryKey: balanceKeys.summary,
    queryFn: async () => {
      const response = await apiClient.get<BalanceSummaryDto>("/api/me/balance-summary");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch balance summary");
      }
      return response.data;
    },
  });
}

/**
 * Fetch balance for a specific group
 *
 * This is a derived hook that uses useBalanceSummary under the hood.
 */
export function useGroupBalance(groupId: string | undefined) {
  const { data: summary, isLoading, error } = useBalanceSummary();

  const groupBalance = summary?.groups.find((g) => g.groupId === groupId);

  return {
    data: groupBalance,
    isLoading,
    error,
  };
}
